/**
 * Password complexity policy — mirror of `backend/src/gudwin/api/password_policy.py`
 * (task 18 §2.4). Both source files MUST stay in sync; drift is caught by
 * matching parametrised tests in `passwordPolicy.test.ts` and the backend's
 * `test_password_policy.py`.
 *
 * Rules (HITL decision, see `docs/tasks/18_security-audit.md`):
 * - `password.length >= 12`
 * - at least 3 of 4 character classes: lower / upper / digit / special.
 *
 * Class semantics — Unicode-aware so a Cyrillic-only password counts the same
 * as Latin:
 * - `lower` — `\p{Ll}` (any "lowercase letter" Unicode property).
 * - `upper` — `\p{Lu}` (any "uppercase letter").
 * - `digit` — `\p{Nd}` (decimal digit, any script).
 * - `special` — any code point that is **not** a letter, **not** a number,
 *   and **not** whitespace. Mirrors backend `not c.isalnum() and not c.isspace()`.
 *
 * No external deps (zxcvbn-style entropy explicitly rejected by HITL).
 */

export const MIN_LENGTH = 12;
export const MIN_CLASSES = 3;

export type PasswordClass = "lower" | "upper" | "digit" | "special";
export const ALL_CLASSES: readonly PasswordClass[] = ["lower", "upper", "digit", "special"];

const RE_LOWER = /\p{Ll}/u;
const RE_UPPER = /\p{Lu}/u;
const RE_DIGIT = /\p{Nd}/u;
// "Not letter, not number, not whitespace" — mirror of backend `not isalnum and not isspace`.
const RE_SPECIAL = /[^\p{L}\p{N}\s]/u;

export interface PasswordPolicyResult {
  lengthOk: boolean;
  length: number;
  classes: {
    lower: boolean;
    upper: boolean;
    digit: boolean;
    special: boolean;
  };
  classesPresent: PasswordClass[];
  classCount: number;
  classesOk: boolean;
  ok: boolean;
}

export function evaluatePassword(password: string): PasswordPolicyResult {
  const classes = {
    lower: RE_LOWER.test(password),
    upper: RE_UPPER.test(password),
    digit: RE_DIGIT.test(password),
    special: RE_SPECIAL.test(password),
  };
  const present = ALL_CLASSES.filter((c) => classes[c]);
  // Use code-point length to be consistent with backend `len(password)` on Python str.
  const length = [...password].length;
  const lengthOk = length >= MIN_LENGTH;
  const classCount = present.length;
  const classesOk = classCount >= MIN_CLASSES;
  return {
    lengthOk,
    length,
    classes,
    classesPresent: present,
    classCount,
    classesOk,
    ok: lengthOk && classesOk,
  };
}
